import { quikqAuthService } from './quikqAuth';
import { getCachedStations, setCachedStations } from './stationsCache';

export interface QuikQStation {
  startCode: string;
  stationCode: string;
  network: string;
  stationName: string;
  store: string;
  stationType: string;
  latitude: number; // Changed from lat to match MapRoutes
  longitude: number; // Changed from lon to match MapRoutes
  lat?: number; // Added for backward compatibility
  lon?: number; // Added for backward compatibility
  address: string;
  city: string;
  state: string;
  zip: string;
  miles: number;
  rfid: boolean;
  affiliates: Array<{
    afCode: string;
    afType: string;
    afName: string;
  }>;
  amenities: Array<{
    aType: string;
    aName: string;
  }>;
  repricing: Array<{
    prodCode: string;
    retailPrice: number;
    adjPrice: number;
    adjType: string;
  }>;
}

export interface QuikQStationsResponse {
  status: string;
  data: {
    center: {
      lat: number;
      lon: number;
    };
    stations: QuikQStation[];
  };
}

export interface QuikQStationsParams {
  startCode?: string; // Made optional for location-based search
  stationCode?: string;
  miles?: number;
  latitude?: number;
  longitude?: number;
  radius?: number; // Added for radius-based search
  city?: string;
  state?: string;
  zipCode?: string;
  /** Internal: skip the persistent IndexedDB cache when refreshing in background. */
  _bypassPersistentCache?: boolean;
}

// User Profile Types
export interface QuikQUserProfile {
  userId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  startCode: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLogin: string;
  // Additional fields that might be returned by API
  companyName?: string;
  status?: string;
  driverType?: string;
  accountCode?: string;
}

// Carrier Info Types
export interface CarrierInfo {
  startCode: string;
  carrierName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  isActive: boolean;
  // Additional fields that might be returned by API
  drivers?: DriverInfo[];
  trailers?: Array<{
    trailerId: string;
    trailerNumber: string;
    isActive: boolean;
  }>;
  qcodes?: Array<{
    qcode: string;
    amount: number;
    qcodeStatus: string;
    createdAt: string;
  }>;
  cards?: CardInfo[];
  remainingCredit?: number;
}

// Cards Types
export interface CardInfo {
  startCode: number;
  cardNo: string;
  cardType: 'Driver' | 'Unit' | '';
  assetId: string;
  status: 'Active' | 'Inactive' | 'Damaged';
}

export interface CardsResponse {
  status: string;
  data: {
    cards: CardInfo[];
  };
}

// Savings Types
export interface SavingsTransaction {
  transactionId: string;
  cardNo: string;
  stationCode: string;
  stationName: string;
  transactionDate: string;
  fuelType: string;
  gallons: number;
  retailPrice: number;
  adjustedPrice: number;
  savings: number;
  totalAmount: number;
  location: {
    address: string;
    city: string;
    state: string;
    zip: string;
  };
}

export interface SavingsSummary {
  totalSaved: number;
  totalSpent: number;
  totalTransactions: number;
  averageSavings: number;
  savingsByStation: Array<{
    stationCode: string;
    stationName: string;
    totalSaved: number;
    totalSpent: number;
    transactionCount: number;
    averageSavings: number;
  }>;
  savingsByCard: Array<{
    cardNo: string;
    cardType: string;
    totalSaved: number;
    totalSpent: number;
    transactionCount: number;
    averageSavings: number;
  }>;
}

export interface SavingsResponse {
  status: string;
  data: {
    transactions: SavingsTransaction[];
    summary: SavingsSummary;
  };
}

export interface DriverInfo {
  driverId: string;
  firstName: string;
  lastName: string;
  licenseNumber: string;
  isActive: boolean;
  // Additional fields that might be returned by API
  status?: string;
  dob?: string;
  hireDate?: string;
  license?: string;
  licenseState?: string;
  unitAssigned?: string;
  driverType?: string;
  emailAddress?: string;
  phone?: Array<{
    phone: string;
    type: string;
  }>;
  address?: Array<{
    line1: string;
    city: string;
    state: string;
    zip: string;
  }>;
  division?: string;
}

export interface AdminStats {
  totalStations: number;
  activeStations: number;
  totalTransactions: number;
  totalRevenue: number;
  activeDrivers: number;
  activeTrailers: number;
  activeQCodes: number;
  // Additional fields that might be returned by API
  totalCarriers?: number;
  totalDrivers?: number;
}

export interface UserData {
  carrier: CarrierInfo;
  drivers: DriverInfo[];
  trailers: Array<{
    trailerId: string;
    trailerNumber: string;
    isActive: boolean;
  }>;
  qcodes: Array<{
    qcode: string;
    amount: number;
    qcodeStatus: string;
    createdAt: string;
  }>;
  stats: AdminStats;
}

// QuikQ Transaction interfaces based on API documentation
export interface QuikQTransaction {
  seq: number;
  startCode: number;
  transNumber: number;
  beginDate: string;
  cashier: string;
  driver: string;
  unit: string;
  trailer: string;
  order: string;
  complete: string;
  station: string;
  magCard: string;
  magCardType: string;
  rfidTagNumber: string;
  qCheckNumber: string;
  qCodeAmount: string;
  qCheckFee: string;
  qCodeNumber: string;
  transType: string;
  disReprice: number;
  disRepriceEff: string;
  dataCapture: {
    driverCode: string;
    driverName: string;
    licenseNumber: string;
    licenseState: string;
    hub: string;
    odometer: string;
    orderNumber: string;
    reeferHours: string;
    trailerNumber: string;
    trailerNumber2: string;
    unitNumber: string;
    promptedUnit: string;
    workOrder: string;
  };
  imageExists: boolean;
  subTransactions: QuikQSubTransactionItem[];
  voidDetails: QuikQVoidDetails[];
}

// Interface for wrapped transaction objects returned by the API
export interface QuikQWrappedTransaction {
  transaction: QuikQTransaction;
}

// Interface for wrapped subTransaction objects returned by the API
export interface QuikQWrappedSubTransaction {
  subTransaction: QuikQSubTransaction;
}

// Union type for both direct and wrapped transactions
export type QuikQTransactionItem = QuikQTransaction | QuikQWrappedTransaction;

// Union type for both direct and wrapped subTransactions
export type QuikQSubTransactionItem = QuikQSubTransaction | QuikQWrappedSubTransaction;

export interface QuikQSubTransaction {
  seq: number;
  subTransNum: number;
  station: string;
  cashier: string;
  completion: string;
  confirmation: string;
  voided: boolean;
  eventType: string;
  eventDate: string;
  postDate: string;
  totalTax: number;
  subTotal: number;
  adjSubTotal: number;
  creditType: string;
  purSubTranNum: number;
  productDetails: QuikQProductDetail[];
  voidProducts: QuikQVoidProduct[];
}

export interface QuikQProductDetail {
  seq: number;
  productCode: number;
  quantity: number;
  unitCost: number;
  discount: number;
  tax1: number;
  tax2: number;
  productTotal: number;
  adjustedPrice: number;
  priceUsed: string;
  carrierFee: number;
}

export interface QuikQVoidProduct {
  productCode: number;
  voidAmount: number;
}

export interface QuikQVoidDetails {
  station: string;
  cashier: string;
  confirmation: string;
  voidDate: string;
  voidTotal: number;
  voidProducts: QuikQVoidProduct[];
}

export interface QuikQTransactionReport {
  transactionCount: number;
  carrierCount: number;
  truckStopCount: number;
  transactions: QuikQTransactionItem[];
  carriers: Array<{
    startCode: number;
    carrier: string;
    programCode: string;
    sendFees: number;
  }>;
  truckStops: Array<{
    station: string;
    merchant: string;
    name: string;
    storeNumber: string;
    parentCode: string;
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    timeZone: string;
    smartQ: boolean;
  }>;
  products: Array<{
    productCode: number;
    productName: string;
    groupCode: string;
    groupName: string;
  }>;
}

export interface QuikQTransactionResponse {
  status: string;
  data: {
    report?: QuikQTransactionReport;
    transactions?: QuikQTransaction[];
    data?: {
      transactions?: QuikQTransaction[];
    };
  };
  transactions?: QuikQTransaction[];
}

class QuikQApiService {
  private readonly API_BASE_URL = 'https://api.quikq.com/v1'; // Real QuikQ API
  /** Mock stations only when explicitly enabled (local testing without QuikQ credentials). */
  private readonly OFFLINE_MODE = process.env.REACT_APP_OFFLINE_MODE === 'true';
  private readonly STATIONS_TTL_MS = 60 * 1000;
  private readonly ENDPOINT_FALLBACK_STATUSES = new Set([404, 405, 501]);
  private preferredStationsEndpoint: string | null =
    typeof window !== 'undefined' ? localStorage.getItem('nsf_preferred_stations_endpoint') : null;
  private stationsCache = new Map<string, { ts: number; data: QuikQStation[] }>();
  private stationsInflight = new Map<string, Promise<QuikQStation[]>>();
  /** At most one background SWR refresh per cache key at a time. */
  private persistentCacheRefreshInflight = new Map<string, Promise<void>>();

  // Helper to handle 401/403 responses and auto-logout
  private async handleResponse(response: Response): Promise<Response> {
    if (response.status === 401 || response.status === 403) {
      console.warn('🔐 Authentication failed or forbidden. Auto-logout...');
      quikqAuthService.logout();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
    return response;
  }

  private buildStationsCacheKey(params: QuikQStationsParams, startCode: string): string {
    const roundedLat = typeof params.latitude === 'number' ? params.latitude.toFixed(2) : '';
    const roundedLng = typeof params.longitude === 'number' ? params.longitude.toFixed(2) : '';
    const radius = params.radius ?? params.miles ?? '';
    return [
      startCode,
      params.stationCode || '*ANY',
      radius,
      roundedLat,
      roundedLng,
      params.city || '',
      params.state || '',
      params.zipCode || '',
    ].join('|');
  }

  /**
   * Deduped stale-while-revalidate refresh for IndexedDB station cache entries.
   * Only one background fetch runs per cache key until it completes.
   */
  private schedulePersistentCacheRefresh(
    cacheKey: string,
    params: QuikQStationsParams,
    options: { signal?: AbortSignal } | undefined,
    startCode: string,
  ): void {
    if (this.persistentCacheRefreshInflight.has(cacheKey)) {
      return;
    }

    const refreshPromise = (async () => {
      try {
        const fresh = await this.getStations({ ...params, _bypassPersistentCache: true }, options);
        if (Array.isArray(fresh) && fresh.length > 0) {
          await setCachedStations(cacheKey, fresh, startCode);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.warn('stationsCache: background refresh failed', error);
        }
      }
    })();

    this.persistentCacheRefreshInflight.set(cacheKey, refreshPromise);
    void refreshPromise.finally(() => {
      if (this.persistentCacheRefreshInflight.get(cacheKey) === refreshPromise) {
        this.persistentCacheRefreshInflight.delete(cacheKey);
      }
    });
  }

  private rememberPreferredStationsEndpoint(endpoint: string): void {
    this.preferredStationsEndpoint = endpoint;
    if (typeof window !== 'undefined') {
      localStorage.setItem('nsf_preferred_stations_endpoint', endpoint);
    }
  }

  private clearPreferredStationsEndpoint(): void {
    this.preferredStationsEndpoint = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('nsf_preferred_stations_endpoint');
    }
  }

  // Get stations with offline mode support
  async getStations(params: QuikQStationsParams, options?: { signal?: AbortSignal }): Promise<QuikQStation[]> {
    // Offline mode for testing
    if (this.OFFLINE_MODE) {
      // console.log('🔧 Offline mode: Mocking QuikQ stations');
      return this.getMockStations(params);
    }

    try {
      let authHeaders = await quikqAuthService.getGetAuthHeaders();
      
      // Get current user to get startCode if not provided
      const currentUser = quikqAuthService.getCurrentUser();
      // console.log('🔍 Current user from auth service:', currentUser);
      
      const startCode = params.startCode || currentUser?.startCode;
      
      // console.log('🔍 Using startCode:', startCode, 'from params:', params.startCode, 'or user:', currentUser?.startCode);
      
      if (!startCode) {
        console.error('❌ No startCode available. Current user:', currentUser);
        throw new Error('Start code is required. Please log in with a valid account.');
      }

      const cacheKey = this.buildStationsCacheKey(params, startCode);
      const cached = this.stationsCache.get(cacheKey);
      if (cached && Date.now() - cached.ts < this.STATIONS_TTL_MS) {
        return cached.data;
      }

      // Persistent (IndexedDB) cache — serves the 9 AM / 5 PM Chicago schedule.
      // We only consult it when the caller hasn't explicitly opted out (so the
      // stale-while-revalidate background refresh below can force a real
      // network fetch).
      const bypassPersistent = params._bypassPersistentCache === true;
      if (!bypassPersistent) {
        try {
          const persisted = await getCachedStations(cacheKey);
          if (persisted) {
            // Warm the in-memory cache so subsequent calls in this tab are instant.
            this.stationsCache.set(cacheKey, { ts: persisted.cachedAt, data: persisted.data });

            if (persisted.isFresh) {
              return persisted.data;
            }

            // Stale-while-revalidate: return stale data immediately, refresh in
            // the background so the next query gets fresh data.
            this.schedulePersistentCacheRefresh(cacheKey, params, options, startCode);

            return persisted.data;
          }
        } catch (error) {
          // Cache failures must never block the foreground fetch.
          console.warn('stationsCache: lookup failed, falling through to network', error);
        }
      }

      const inflight = this.stationsInflight.get(cacheKey);
      if (inflight) {
        return inflight;
      }

      const queryParams = new URLSearchParams({
        startCode,
        stationCode: params.stationCode || '*ANY',
        ...(params.miles && { miles: params.miles.toString() }),
        ...(params.radius && { miles: params.radius.toString() }), // Use radius as miles
        ...(params.latitude && { latitude: params.latitude.toString() }),
        ...(params.longitude && { longitude: params.longitude.toString() }),
        ...(params.city && { city: params.city }),
        ...(params.state && { state: params.state }),
        ...(params.zipCode && { zipCode: params.zipCode }),
      });

      const allEndpoints = [
        '/carriers/stations',
        '/stations',
        '/drivers/stations',
        '/user/stations'
      ];
      const preferredEndpointAtStart = this.preferredStationsEndpoint;
      const endpoints: string[] = preferredEndpointAtStart
        ? [preferredEndpointAtStart, ...allEndpoints.filter(ep => ep !== preferredEndpointAtStart)]
        : allEndpoints;

      const requestPromise = (async (): Promise<QuikQStation[]> => {
        let lastError: Error | null = null;

        for (const endpoint of endpoints) {
          try {
            const url = `${this.API_BASE_URL}${endpoint}?${queryParams}`;
            let response = await fetch(url, {
              method: 'GET',
              headers: authHeaders,
              signal: options?.signal,
            });

            // QuikQ rotates the JWT via a response header on successful calls;
            // persisting it keeps the session alive past the original 30-min TTL.
            quikqAuthService.consumeRefreshedAuthToken(response);

            // Recover once from idle-session expiry without forcing immediate logout.
            if (response.status === 401) {
              const refreshedToken = await quikqAuthService.refreshToken();
              if (!refreshedToken) {
                await this.handleResponse(response);
              } else {
                authHeaders = await quikqAuthService.getGetAuthHeaders();
                response = await fetch(url, {
                  method: 'GET',
                  headers: authHeaders,
                  signal: options?.signal,
                });
                quikqAuthService.consumeRefreshedAuthToken(response);
              }
            }

            await this.handleResponse(response);

            if (!response.ok) {
              const shouldFallback = this.ENDPOINT_FALLBACK_STATUSES.has(response.status);
              const isPreferredEndpoint = !!preferredEndpointAtStart && endpoint === preferredEndpointAtStart;
              if (isPreferredEndpoint) {
                this.clearPreferredStationsEndpoint();
              }
              lastError = new Error(`Failed to fetch stations from ${endpoint}: ${response.status}`);
              if (!shouldFallback) {
                throw lastError;
              }
              continue;
            }

            const responseText = await response.text();
            if (!responseText) {
              continue;
            }

            let data: QuikQStationsResponse;
            try {
              data = JSON.parse(responseText);
            } catch {
              continue;
            }

            if (data.status !== 'success') {
              // Some QuikQ deployments report an expired session as HTTP 200 with
              // status: "error" instead of 401. Detect those, refresh, and retry
              // once before silently falling through to the next endpoint.
              const errMsg = String((data as any)?.message ?? (data as any)?.error ?? '').toLowerCase();
              const looksLikeAuthError =
                errMsg.includes('token') ||
                errMsg.includes('expired') ||
                errMsg.includes('unauthorized') ||
                errMsg.includes('authentication') ||
                errMsg.includes('session');

              if (looksLikeAuthError) {
                const refreshedToken = await quikqAuthService.refreshToken();
                if (refreshedToken) {
                  authHeaders = await quikqAuthService.getGetAuthHeaders();
                  const retryResponse = await fetch(url, {
                    method: 'GET',
                    headers: authHeaders,
                    signal: options?.signal,
                  });
                  quikqAuthService.consumeRefreshedAuthToken(retryResponse);
                  await this.handleResponse(retryResponse);
                  if (retryResponse.ok) {
                    const retryText = await retryResponse.text();
                    if (retryText) {
                      try {
                        const retryData: QuikQStationsResponse = JSON.parse(retryText);
                        if (retryData.status === 'success') {
                          data = retryData;
                        }
                      } catch {
                        // fall through to continue
                      }
                    }
                  }
                }
              }

              if (data.status !== 'success') {
                continue;
              }
            }

            const rawTransformed = (data.data?.stations || []).map(station => ({
              ...station,
              latitude: station.latitude || station.lat || 0,
              longitude: station.longitude || station.lon || 0,
            }));
            const transformedStations = rawTransformed;

            this.rememberPreferredStationsEndpoint(endpoint);
            this.stationsCache.set(cacheKey, { ts: Date.now(), data: transformedStations });
            // Persist to IndexedDB so the next page load / day's first request
            // can serve instantly without hitting the slow QuikQ API. Writes
            // are fire-and-forget; failures must not block the fetch path.
            if (transformedStations.length > 0) {
              void setCachedStations(cacheKey, transformedStations, startCode);
            }
            return transformedStations;
          } catch (error: any) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              throw error;
            }

            const isPreferredEndpoint = !!preferredEndpointAtStart && endpoint === preferredEndpointAtStart;
            if (typeof error?.message === 'string' && error.message.includes('Failed to fetch stations from')) {
              const statusMatch = error.message.match(/:\s*(\d+)\s*$/);
              const statusCode = statusMatch ? Number(statusMatch[1]) : NaN;
              if (isPreferredEndpoint) {
                this.clearPreferredStationsEndpoint();
              }
              if (this.ENDPOINT_FALLBACK_STATUSES.has(statusCode)) {
                lastError = error instanceof Error ? error : new Error('Unknown error');
                continue;
              }
            }

            lastError = error instanceof Error ? error : new Error('Unknown error');
            throw lastError;
          }
        }

        if (lastError) {
          throw lastError;
        }
        throw new Error('No stations endpoints available');
      })();

      this.stationsInflight.set(cacheKey, requestPromise);
      try {
        return await requestPromise;
      } finally {
        this.stationsInflight.delete(cacheKey);
      }
    } catch (error) {
      console.error('QuikQ API Error:', error);
      if (this.OFFLINE_MODE) {
        console.warn('🔧 OFFLINE_MODE: returning mock stations after API error.');
        return this.getMockStations(params);
      }
      throw error;
    }
  }

  // Get station details
  async getStationDetails(startCode: string, stationCode: string, unitId: string) {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startCode,
        stationCode,
        unitId,
      });

      const response = await fetch(`${this.API_BASE_URL}/carriers/stationsDetail?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch station details: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Failed to fetch station details');
      }

      return data.data;
    } catch (error) {
      console.error('QuikQ API Error:', error);
      throw error;
    }
  }

  // Convert city/state/zip to geo coordinates
  async getCityStateZipGeo(city?: string, state?: string, zipCode?: string) {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        ...(city && { city }),
        ...(state && { state }),
        ...(zipCode && { zipCode }),
      });

      const response = await fetch(`${this.API_BASE_URL}/carriers/cityStateZipGeo?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to get geo coordinates: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Failed to get geo coordinates');
      }

      return data.data;
    } catch (error) {
      console.error('QuikQ API Error:', error);
      throw error;
    }
  }

  // Get transactions
  async getTransactions(startCode: string, startDt: string, endDt: string) {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startCode,
        startDt,
        endDt,
      });

      const response = await fetch(`${this.API_BASE_URL}/reports/transactions?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Failed to fetch transactions');
      }

      return data.data;
    } catch (error) {
      console.error('QuikQ API Error:', error);
      throw error;
    }
  }

  // Get carrier partner transactions
  async getCarrierPartnerTransactions(startCode: string, startDt: string, endDt: string) {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startCode,
        startDt,
        endDt,
      });

      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/carrierPartners?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch carrier partner transactions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Failed to fetch carrier partner transactions');
      }

      return data.data;
    } catch (error) {
      console.error('QuikQ API Error:', error);
      throw error;
    }
  }

  // Get station transactions
  async getStationTransactions(startCode: string, startDt: string, endDt: string) {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startCode,
        startDt,
        endDt,
      });

      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/stations?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch station transactions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error('Failed to fetch station transactions');
      }

      return data.data;
    } catch (error) {
      console.error('QuikQ API Error:', error);
      throw error;
    }
  }

  // Mock stations for offline testing
  private getMockStations(params: QuikQStationsParams): QuikQStation[] {
    // console.log('🔧 Mocking QuikQ stations around:', params.latitude, params.longitude);
    
    // Generate stations around the given coordinates
    const baseLat = params.latitude || 34.0522;
    const baseLng = params.longitude || -118.2437;
    const radius = params.radius || 50;
    
    const mockStations: QuikQStation[] = [
      {
        startCode: 'MOCK001',
        stationCode: 'QS001',
        network: 'QuikQ Network',
        stationName: 'QuikQ Downtown Station',
        store: 'Downtown Store',
        stationType: 'Full Service',
        latitude: baseLat + 0.01,
        longitude: baseLng + 0.01,
        address: '123 Main Street',
        city: 'Downtown',
        state: 'CA',
        zip: '90210',
        miles: Math.round(Math.random() * 5) + 1,
        rfid: true,
        affiliates: [
          { afCode: 'SHELL', afType: 'BRAND', afName: 'Shell' },
          { afCode: 'EXXON', afType: 'BRAND', afName: 'Exxon' }
        ],
        amenities: [
          { aType: 'SERVICE', aName: 'ATM' },
          { aType: 'SERVICE', aName: 'Car Wash' },
          { aType: 'SERVICE', aName: 'Convenience Store' }
        ],
        repricing: [
          { prodCode: 'REG', retailPrice: 3.50, adjPrice: 3.25, adjType: 'DISCOUNT' },
          { prodCode: 'PREM', retailPrice: 3.80, adjPrice: 3.55, adjType: 'DISCOUNT' },
          { prodCode: 'DIESEL', retailPrice: 4.20, adjPrice: 3.95, adjType: 'DISCOUNT' }
        ],
      },
      {
        startCode: 'MOCK001',
        stationCode: 'QS002',
        network: 'QuikQ Network',
        stationName: 'QuikQ Express Station',
        store: 'Express Store',
        stationType: 'Self Service',
        latitude: baseLat - 0.008,
        longitude: baseLng + 0.015,
        address: '456 Oak Avenue',
        city: 'Midtown',
        state: 'CA',
        zip: '90211',
        miles: Math.round(Math.random() * 5) + 1,
        rfid: false,
        affiliates: [
          { afCode: 'MOBIL', afType: 'BRAND', afName: 'Mobil' }
        ],
        amenities: [
          { aType: 'SERVICE', aName: 'ATM' }
        ],
        repricing: [
          { prodCode: 'REG', retailPrice: 3.45, adjPrice: 3.20, adjType: 'DISCOUNT' },
          { prodCode: 'PREM', retailPrice: 3.75, adjPrice: 3.50, adjType: 'DISCOUNT' }
        ],
      },
      {
        startCode: 'MOCK001',
        stationCode: 'QS003',
        network: 'QuikQ Network',
        stationName: 'QuikQ Highway Station',
        store: 'Highway Store',
        stationType: 'Truck Stop',
        latitude: baseLat + 0.02,
        longitude: baseLng - 0.012,
        address: '789 Highway 101',
        city: 'Highway City',
        state: 'CA',
        zip: '90212',
        miles: Math.round(Math.random() * 5) + 1,
        rfid: true,
        affiliates: [
          { afCode: 'SHELL', afType: 'BRAND', afName: 'Shell' },
          { afCode: 'BP', afType: 'BRAND', afName: 'BP' }
        ],
        amenities: [
          { aType: 'SERVICE', aName: 'ATM' },
          { aType: 'SERVICE', aName: 'Restaurant' },
          { aType: 'SERVICE', aName: 'Shower' },
          { aType: 'SERVICE', aName: 'Truck Parking' }
        ],
        repricing: [
          { prodCode: 'REG', retailPrice: 3.60, adjPrice: 3.35, adjType: 'DISCOUNT' },
          { prodCode: 'PREM', retailPrice: 3.90, adjPrice: 3.65, adjType: 'DISCOUNT' },
          { prodCode: 'DIESEL', retailPrice: 4.30, adjPrice: 4.05, adjType: 'DISCOUNT' },
          { prodCode: 'BIODIESEL', retailPrice: 4.50, adjPrice: 4.25, adjType: 'DISCOUNT' }
        ],
      },
      {
        startCode: 'MOCK001',
        stationCode: 'QS004',
        network: 'QuikQ Network',
        stationName: 'QuikQ Suburban Station',
        store: 'Suburban Store',
        stationType: 'Full Service',
        latitude: baseLat - 0.015,
        longitude: baseLng - 0.008,
        address: '321 Suburban Drive',
        city: 'Suburbia',
        state: 'CA',
        zip: '90213',
        miles: Math.round(Math.random() * 5) + 1,
        rfid: true,
        affiliates: [
          { afCode: 'CHEVRON', afType: 'BRAND', afName: 'Chevron' }
        ],
        amenities: [
          { aType: 'SERVICE', aName: 'ATM' },
          { aType: 'SERVICE', aName: 'Car Wash' },
          { aType: 'SERVICE', aName: 'Convenience Store' },
          { aType: 'SERVICE', aName: 'Air Pump' }
        ],
        repricing: [
          { prodCode: 'REG', retailPrice: 3.55, adjPrice: 3.30, adjType: 'DISCOUNT' },
          { prodCode: 'PREM', retailPrice: 3.85, adjPrice: 3.60, adjType: 'DISCOUNT' },
          { prodCode: 'DIESEL', retailPrice: 4.25, adjPrice: 4.00, adjType: 'DISCOUNT' }
        ],
      },
      {
        startCode: 'MOCK001',
        stationCode: 'QS005',
        network: 'QuikQ Network',
        stationName: 'QuikQ Airport Station',
        store: 'Airport Store',
        stationType: 'Premium Service',
        latitude: baseLat + 0.025,
        longitude: baseLng + 0.025,
        address: '654 Airport Boulevard',
        city: 'Airport City',
        state: 'CA',
        zip: '90214',
        miles: Math.round(Math.random() * 5) + 1,
        rfid: true,
        affiliates: [
          { afCode: 'SHELL', afType: 'BRAND', afName: 'Shell' },
          { afCode: 'EXXON', afType: 'BRAND', afName: 'Exxon' },
          { afCode: 'MOBIL', afType: 'BRAND', afName: 'Mobil' }
        ],
        amenities: [
          { aType: 'SERVICE', aName: 'ATM' },
          { aType: 'SERVICE', aName: 'Car Wash' },
          { aType: 'SERVICE', aName: 'Convenience Store' },
          { aType: 'SERVICE', aName: 'Air Pump' },
          { aType: 'SERVICE', aName: 'Vacuum' }
        ],
        repricing: [
          { prodCode: 'REG', retailPrice: 3.70, adjPrice: 3.45, adjType: 'DISCOUNT' },
          { prodCode: 'PREM', retailPrice: 4.00, adjPrice: 3.75, adjType: 'DISCOUNT' },
          { prodCode: 'DIESEL', retailPrice: 4.40, adjPrice: 4.15, adjType: 'DISCOUNT' },
          { prodCode: 'E85', retailPrice: 3.20, adjPrice: 2.95, adjType: 'DISCOUNT' }
        ],
      },
    ];
    
    // Filter stations within the specified radius (simplified calculation)
    const filteredStations = mockStations.filter(station => {
      const distance = Math.sqrt(
        Math.pow(station.latitude - baseLat, 2) + 
        Math.pow(station.longitude - baseLng, 2)
      ) * 69; // Rough conversion to miles
      return distance <= radius;
    });
    
    // console.log(`✅ Generated ${filteredStations.length} mock stations within ${radius} miles`);
    return filteredStations;
  }

  // Get user profile
  async getUserProfile(): Promise<QuikQUserProfile> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      const currentUser = quikqAuthService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      const startCode = currentUser.startCode;
      
      // console.log('🔍 Fetching user profile for startCode:', startCode);
      
      // Try to get user information from various endpoints
      // First, try to get driver information using the carriers endpoint
      try {
        const driversResponse = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/drivers`, {
          method: 'GET',
          headers: authHeaders,
        });
        
        if (driversResponse.ok) {
          const driversData = await driversResponse.json();
          // console.log('🔍 Drivers data:', driversData);
          
          // Find the current driver in the list
          const currentDriver = driversData.data?.drivers?.find((driver: any) => 
            driver.userId === currentUser.userId || 
            driver.email === currentUser.userId ||
            driver.driverId === currentUser.userId
          );
          
          if (currentDriver) {
            // Get specific driver details
            const driverDetailsResponse = await fetch(`${this.API_BASE_URL}/carriers/drivers/${currentDriver.driverId}?startCode=${startCode}`, {
              method: 'GET',
              headers: authHeaders,
            });
            
            if (driverDetailsResponse.ok) {
              const driverDetails = await driverDetailsResponse.json();
              // console.log('🔍 Driver details:', driverDetails);
              
              return {
                userId: currentDriver.driverId || currentUser.userId,
                username: (currentDriver.firstName || 'User') + ' ' + (currentDriver.lastName || 'Name'),
                email: currentDriver.emailAddress || currentUser.userId,
                firstName: currentDriver.firstName || 'User',
                lastName: currentDriver.lastName || 'Name',
                startCode: startCode || '',
                role: 'USER',
                isActive: currentDriver.isActive || true,
                createdAt: currentDriver.hireDate || new Date().toISOString(),
                lastLogin: new Date().toISOString(),
                companyName: currentDriver.division || 'NorthStar Fuel',
                status: currentDriver.status || 'ACTIVE',
                driverType: currentDriver.driverType || 'REGULAR',
              };
            }
          }
        }
      } catch (error) {
        // console.log('🔍 Could not fetch driver info, trying carrier info...');
      }
      
      // Try to get carrier information
      try {
        const carrierResponse = await fetch(`${this.API_BASE_URL}/carriers?startCode=${startCode}`, {
          method: 'GET',
          headers: authHeaders,
        });
        
        if (carrierResponse.ok) {
          const carrierData = await carrierResponse.json();
          // console.log('🔍 Carrier data:', carrierData);
          
          return {
            userId: currentUser.userId,
            username: carrierData.data?.carrierName || currentUser.userId,
            email: carrierData.data?.email || currentUser.userId,
            firstName: 'User',
            lastName: 'Name',
            startCode: startCode || '',
            role: 'USER',
            isActive: carrierData.data?.isActive || true,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            companyName: carrierData.data?.carrierName || 'NorthStar Fuel',
            status: carrierData.data?.isActive ? 'ACTIVE' : 'INACTIVE',
            accountCode: startCode || '',
          };
        }
      } catch (error) {
        // console.log('🔍 Could not fetch carrier info, trying generic endpoints...');
      }
      
      // Fallback: try generic profile endpoints
      const endpoints = [
        '/users/profile',
        '/user/profile', 
        '/profile',
        '/drivers/profile',
        '/carriers/profile'
      ];
      
      let lastError: Error | null = null;
      
      for (const endpoint of endpoints) {
        try {
          // console.log(`🔍 Trying endpoint: ${endpoint}`);
          const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
            method: 'GET',
            headers: authHeaders,
          });

          if (response.ok) {
            const data = await response.json();
            // console.log(`✅ Profile endpoint ${endpoint} successful:`, data);
            return data;
          } else {
            // console.log(`❌ Profile endpoint ${endpoint} failed: ${response.status} ${response.statusText}`);
            lastError = new Error(`Failed to fetch user profile from ${endpoint}: ${response.status}`);
          }
        } catch (error) {
          // console.log(`❌ Profile endpoint ${endpoint} error:`, error);
          lastError = error instanceof Error ? error : new Error('Unknown error');
        }
      }
      
      // If all endpoints failed, throw the last error
      if (lastError) {
        throw lastError;
      }
      
      throw new Error('No profile endpoints available');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Return basic user data from auth service as fallback
      const currentUser = quikqAuthService.getCurrentUser();
      if (currentUser) {
        return {
          userId: currentUser.userId,
          username: currentUser.userId,
          email: currentUser.userId,
          firstName: 'User',
          lastName: 'Profile',
          startCode: currentUser.startCode || '',
          role: currentUser.role || 'DRIVER',
          isActive: true,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          companyName: 'NorthStar Fuel',
          status: 'ACTIVE',
        };
      }
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(profileData: Partial<QuikQUserProfile>): Promise<QuikQUserProfile> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      const response = await fetch(`${this.API_BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update user profile: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Get user data (carrier info, drivers, trailers, etc.)
  async getUserData(): Promise<UserData> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      const currentUser = quikqAuthService.getCurrentUser();
      const userRole = currentUser?.role || 'DRIVER';
      
      // console.log('🔍 Fetching user data for role:', userRole);
      
      // Try different endpoints based on role
      const endpoints = [
        '/users/data',
        '/user/data',
        '/drivers/data',
        '/carriers/data',
        '/profile/data'
      ];
      
      let lastError: Error | null = null;
      
      for (const endpoint of endpoints) {
        try {
          // console.log(`🔍 Trying user data endpoint: ${endpoint}`);
          const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
            method: 'GET',
            headers: authHeaders,
          });

          if (response.ok) {
            const data = await response.json();
            // console.log(`✅ User data endpoint ${endpoint} successful:`, data);
            return data;
          } else {
            // console.log(`❌ User data endpoint ${endpoint} failed: ${response.status} ${response.statusText}`);
            lastError = new Error(`Failed to fetch user data from ${endpoint}: ${response.status}`);
          }
        } catch (error) {
          // console.log(`❌ User data endpoint ${endpoint} error:`, error);
          lastError = error instanceof Error ? error : new Error('Unknown error');
        }
      }
      
      // If all endpoints failed, throw the last error
      if (lastError) {
        throw lastError;
      }
      
      throw new Error('No user data endpoints available');
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Return mock data for development
      return {
        carrier: {
          startCode: 'MOCK001',
          carrierName: 'Mock Carrier Inc.',
          address: '123 Mock Street',
          city: 'Mock City',
          state: 'CA',
          zip: '90210',
          phone: '(555) 123-4567',
          email: 'info@mockcarrier.com',
          isActive: true,
        },
        drivers: [
          {
            driverId: 'DRIVER001',
            firstName: 'John',
            lastName: 'Doe',
            licenseNumber: 'DL123456789',
            isActive: true,
          },
          {
            driverId: 'DRIVER002',
            firstName: 'Jane',
            lastName: 'Smith',
            licenseNumber: 'DL987654321',
            isActive: true,
          },
        ],
        trailers: [
          {
            trailerId: 'TRAILER001',
            trailerNumber: 'TR-001',
            isActive: true,
          },
          {
            trailerId: 'TRAILER002',
            trailerNumber: 'TR-002',
            isActive: true,
          },
        ],
        qcodes: [
          {
            qcode: 'QC001',
            amount: 100.00,
            qcodeStatus: 'ACTIVE',
            createdAt: '2024-01-01T00:00:00Z',
          },
        ],
        stats: {
          totalStations: 150,
          activeStations: 120,
          totalTransactions: 500,
          totalRevenue: 25000.00,
          activeDrivers: 2,
          activeTrailers: 2,
          activeQCodes: 1,
        },
      };
    }
  }

  // Get driver information for a specific driver
  async getDriverInfo(startCode: string, driverId: string): Promise<DriverInfo> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/drivers/${driverId}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch driver info: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('🔍 Driver info:', data);
      
      return data.data || data;
    } catch (error) {
      console.error('Error fetching driver info:', error);
      throw error;
    }
  }

  // Get all drivers for a carrier
  async getCarrierDrivers(startCode: string): Promise<DriverInfo[]> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/drivers`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch carrier drivers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('🔍 Carrier drivers:', data);
      
      return data.data?.drivers || data.drivers || [];
    } catch (error) {
      console.error('Error fetching carrier drivers:', error);
      throw error;
    }
  }

  // Get carrier information
  async getCarrierInfo(startCode: string): Promise<CarrierInfo> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch carrier info: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('🔍 Carrier info:', data);
      
      return data.data || data;
    } catch (error) {
      console.error('Error fetching carrier info:', error);
      throw error;
    }
  }

  // Get cards for a carrier
  async getCarrierCards(startCode: string): Promise<CardInfo[]> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/cards`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch carrier cards: ${response.status} ${response.statusText}`);
      }

      const data: CardsResponse = await response.json();
      // console.log('🔍 Carrier cards:', data);
      
      return data.data?.cards || [];
    } catch (error) {
      console.error('Error fetching carrier cards:', error);
      throw error;
    }
  }

  // Get specific card details
  async getCardDetails(startCode: string, cardId: string): Promise<CardInfo | null> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/cards/${cardId}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch card details: ${response.status} ${response.statusText}`);
      }

      const data: CardsResponse = await response.json();
      // console.log('🔍 Card details:', data);
      
      return data.data?.cards?.[0] || null;
    } catch (error) {
      console.error('Error fetching card details:', error);
      throw error;
    }
  }

  // Update card status or assignment
  async updateCard(startCode: string, cardId: string, updateData: {
    assetType?: 'Driver' | 'Unit' | '';
    assetId?: string;
    status?: 'Active' | 'Inactive' | 'Damaged';
  }): Promise<void> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const response = await fetch(`${this.API_BASE_URL}/carriers/${startCode}/cards/${cardId}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update card: ${response.status} ${response.statusText}`);
      }

      // console.log('✅ Card updated successfully');
    } catch (error) {
      console.error('Error updating card:', error);
      throw error;
    }
  }

  // Get savings data for a carrier - DEPRECATED: This endpoint doesn't exist
  // Use getQuikQCarrierPartnerTransactions instead and calculate savings from transactions
  async getSavingsData(startCode: string, startDt?: string, endDt?: string, cardNo?: string): Promise<SavingsResponse> {
    throw new Error('Savings endpoint not available. Use getQuikQCarrierPartnerTransactions instead.');
  }

  // Get savings by station - DEPRECATED: Use getQuikQCarrierPartnerTransactions instead
  async getSavingsByStation(startCode: string, startDt?: string, endDt?: string): Promise<SavingsSummary['savingsByStation']> {
    throw new Error('Savings endpoint not available. Use getQuikQCarrierPartnerTransactions instead.');
  }

  // Get savings by card - DEPRECATED: Use getQuikQCarrierPartnerTransactions instead
  async getSavingsByCard(startCode: string, startDt?: string, endDt?: string): Promise<SavingsSummary['savingsByCard']> {
    throw new Error('Savings endpoint not available. Use getQuikQCarrierPartnerTransactions instead.');
  }

  // Get savings transactions - DEPRECATED: Use getQuikQCarrierPartnerTransactions instead
  async getSavingsTransactions(startCode: string, startDt?: string, endDt?: string, cardNo?: string): Promise<SavingsTransaction[]> {
    throw new Error('Savings endpoint not available. Use getQuikQCarrierPartnerTransactions instead.');
  }

  // Get QuikQ transactions for a specific start code
  async getQuikQTransactions(startCode: string = '', startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Y'
      });

      // Use carrierPartners endpoint instead of startCode endpoint
      // This endpoint takes startCode from authentication token
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/carrierPartners?${queryParams}`, {
        method: 'GET',
        headers: {
          ...(await quikqAuthService.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching QuikQ transactions:', error);
      throw error;
    }
  }

  // Get QuikQ transactions for carrier partners (authenticated user)
  async getQuikQCarrierPartnerTransactions(startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Yes'  // Changed from 'Y' to 'Yes' to match Rick's format
      });

      // Use the correct endpoint format that Rick is using
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/carrierPartners?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch QuikQ carrier partner transactions: ${response.status} ${response.statusText}`);
      }

      const data: QuikQTransactionResponse = await response.json();
      // console.log('🔍 QuikQ carrier partner transactions data:', data);
      
      return data;
    } catch (error) {
      console.error('Error fetching QuikQ carrier partner transactions:', error);
      throw error;
    }
  }

  // Get QuikQ transactions by specific transaction ID (Rick's format)
  async getQuikQTransactionById(transactionId: string, startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Yes'
      });

      // Use Rick's exact endpoint format
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/${transactionId}?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch QuikQ transaction ${transactionId}: ${response.status} ${response.statusText}`);
      }

      const data: QuikQTransactionResponse = await response.json();
      // console.log(`🔍 QuikQ transaction ${transactionId} data:`, data);
      
      return data;
    } catch (error) {
      console.error(`Error fetching QuikQ transaction ${transactionId}:`, error);
      throw error;
    }
  }

  // Get QuikQ transactions using Rick's exact format (without transaction ID)
  async getQuikQTransactionsRickFormat(startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Yes'
      });

      // Use Rick's exact endpoint format but without specific transaction ID
      // This should work for getting all transactions for the authenticated user
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch QuikQ transactions: ${response.status} ${response.statusText}`);
      }

      const data: QuikQTransactionResponse = await response.json();
      // console.log('🔍 QuikQ transactions data (Rick format):', data);
      
      return data;
    } catch (error) {
      console.error('Error fetching QuikQ transactions (Rick format):', error);
      throw error;
    }
  }

  // Get QuikQ transactions using Rick's exact format with startCode from user
  async getQuikQTransactionsRickFormatWithStartCode(startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      const currentUser = quikqAuthService.getCurrentUser();
      
      if (!currentUser?.startCode) {
        throw new Error('No startCode available for user');
      }
      
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Yes'
      });

      // Use Rick's exact endpoint format with startCode from user
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/${currentUser.startCode}?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('🔐 Authentication failed (401). Token may be expired or invalid.');
          console.error('🔐 Current user:', currentUser);
          console.error('🔐 Auth headers:', authHeaders);
          throw new Error(`Authentication failed: ${response.status} ${response.statusText}. Please log in again.`);
        }
        throw new Error(`Failed to fetch QuikQ transactions for startCode ${currentUser.startCode}: ${response.status} ${response.statusText}`);
      }

      const data: QuikQTransactionResponse = await response.json();
      // console.log(`🔍 QuikQ transactions data for startCode ${currentUser.startCode}:`, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching QuikQ transactions (Rick format with startCode):', error);
      throw error;
    }
  }

  // Get QuikQ transactions using Rick's exact format with specific transaction ID and date range
  async getQuikQTransactionsRickFormatSpecific(transactionId: string, startDt: string, endDt: string): Promise<QuikQTransactionResponse> {
    try {
      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      const queryParams = new URLSearchParams({
        startDt,
        endDt,
        hideNullElements: 'Yes'
      });

      // Use Rick's exact endpoint format with specific transaction ID
      const response = await fetch(`${this.API_BASE_URL}/reports/transactions/${transactionId}?${queryParams}`, {
        method: 'GET',
        headers: authHeaders,
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('🔐 Authentication failed (401). Token may be expired or invalid.');
          console.error('🔐 Current user:', quikqAuthService.getCurrentUser());
          console.error('🔐 Auth headers:', authHeaders);
          throw new Error(`Authentication failed: ${response.status} ${response.statusText}. Please log in again.`);
        }
        throw new Error(`Failed to fetch QuikQ transactions for transaction ID ${transactionId}: ${response.status} ${response.statusText}`);
      }

      const data: QuikQTransactionResponse = await response.json();
      // console.log(`🔍 QuikQ transactions data for transaction ID ${transactionId}:`, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching QuikQ transactions (Rick format specific):', error);
      throw error;
    }
  }
}

// Export singleton instance
export const quikqApiService = new QuikQApiService();